import { Locale } from "@divespots/contracts";
import { useLayoutEffect } from "react";
import { Navigate, Route, Routes, useMatch, useParams, useSearchParams } from "react-router";
import { AdminApp } from "./admin/AdminApp.tsx";
import { browserLocale, i18n } from "./i18n/index.ts";
import { DiveRoute } from "./divegate/DiveRoute.tsx";
import { DiverKontoRoute, DiverLoginRoute } from "./diver/DiverApp.tsx";
import { MapPage } from "./pages/MapPage.tsx";
import { OfflineBadge, UpdateToast } from "./pwa/PwaStatus.tsx";
import { FavouriteNotice } from "./spot/FavouriteNotice.tsx";

function RedirectToLang() {
  return <Navigate to={`/${browserLocale()}/`} replace />;
}

/** Validates `:lang` and keeps i18n + <html lang> in step with it. */
function useRouteLocale(): Locale | undefined {
  const { lang } = useParams();
  const parsed = Locale.safeParse(lang);
  const locale = parsed.success ? parsed.data : undefined;
  useLayoutEffect(() => {
    if (!locale) return;
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale]);
  return locale;
}

/** `/:lang/admin/*`: the moderator/admin UI (own scrolling page, no map). */
function AdminRoute() {
  const locale = useRouteLocale();
  if (!locale) return <RedirectToLang />;
  return <AdminApp />;
}

/** `/:lang/login` and `/:lang/konto/*`: the diver's own pages (own scrolling page, no map). */
function DiverRoute({ page }: { page: "login" | "konto" }) {
  const locale = useRouteLocale();
  if (!locale) return <RedirectToLang />;
  return page === "login" ? <DiverLoginRoute /> : <DiverKontoRoute />;
}

/** `/:lang/dive/:lake?from=<spotSlug>`: the dive view's own full-screen page, behind the access gate. */
function DiveGateRoute() {
  const locale = useRouteLocale();
  const { lake } = useParams();
  const [search] = useSearchParams();
  if (!locale || !lake) return <RedirectToLang />;
  return <DiveRoute lakeSlug={lake} fromSpotSlug={search.get("from") ?? undefined} />;
}

/** The (persistent) map page for `/:lang/`, `/:lang/spot/:slug` and `/:lang/spot/:slug/3d`. */
function LangLayout() {
  const locale = useRouteLocale();
  const spotMatch = useMatch("/:lang/spot/:slug");
  const spot3dMatch = useMatch("/:lang/spot/:slug/3d");
  const homeMatch = useMatch("/:lang/");

  if (!locale) return <RedirectToLang />;
  if (!spotMatch && !spot3dMatch && !homeMatch) return <Navigate to={`/${locale}/`} replace />;
  return <MapPage slug={(spotMatch ?? spot3dMatch)?.params.slug} view3d={Boolean(spot3dMatch)} />;
}

export function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<RedirectToLang />} />
        <Route path="/:lang/admin/*" element={<AdminRoute />} />
        <Route path="/:lang/login/*" element={<DiverRoute page="login" />} />
        <Route path="/:lang/konto/*" element={<DiverRoute page="konto" />} />
        <Route path="/:lang/dive/:lake" element={<DiveGateRoute />} />
        <Route path="/:lang/*" element={<LangLayout />} />
      </Routes>
      <OfflineBadge />
      <UpdateToast />
      <FavouriteNotice />
    </>
  );
}
